import { useEffect, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { describeSiteError } from "../../api/http"
import { keys } from "../../api/keys"
import { catalogAdminWrites, websiteWrites } from "../../api/mutations"
import { useCatalogGroups, useLatvianWebsites } from "../../api/queries"
import type { Catalog, CatalogGroupRow } from "../../api/types"
import { ActionNotes, Note } from "../../components/Note"
import { useAction } from "../../hooks/useAction"
import { useConfirm } from "../../hooks/useConfirm"
import { useLanguageId } from "../../lib/language"
import NotReady from "./NotReady"

const LIMIT = 200

/**
 * Каталоги: что в них сейчас лежит и назначение товаров из одного в другой.
 *
 * Заменяет старую «Состав каталога сайта» (`CatalogPanel.tsx`), которая умела
 * только целиком прикрепить каталог-источник к сайту, а посмотреть, что уже
 * внутри, было нельзя. Здесь то же самое осталось частным случаем — кнопка
 * «Прикрепить весь каталог-источник» рядом с общим механизмом бэка
 * (`websiteWrites.addCatalog`, один запрос вместо N), а основной путь —
 * выбрать конкретные группы из каталога-источника и добавить только их
 * (`/ai/catalog/attach`, по одному запросу `AttachToCatalogAsync` на группу).
 *
 * Список каталогов передаёт `Publish.tsx` — уже отфильтрован до тех, что
 * касаются Риги (`useRigaCatalogs`), поэтому здесь можно не думать о
 * российских каталогах и каталоге продуктов.
 */
export default function CatalogComposition({
	catalogs,
	defaultCatalogId,
	disabled,
}: {
	catalogs: Catalog[]
	defaultCatalogId: number | null
	disabled: boolean
}) {
	const languageId = useLanguageId()
	const client = useQueryClient()
	const { ask, dialog } = useConfirm()
	const sites = useLatvianWebsites()

	const [catalogId, setCatalogId] = useState<number | null>(defaultCatalogId)
	useEffect(() => {
		if (catalogId !== null && catalogs.some(item => item.id === catalogId)) return
		setCatalogId(defaultCatalogId ?? catalogs[0]?.id ?? null)
	}, [defaultCatalogId, catalogs, catalogId])

	const catalog = catalogs.find(item => item.id === catalogId) ?? null
	const composition = useCatalogGroups(catalogId, languageId)

	const [sourceId, setSourceId] = useState<number | null>(null)
	const others = useMemo(() => catalogs.filter(item => item.id !== catalogId), [catalogs, catalogId])
	const source = others.find(item => item.id === sourceId) ?? null
	const sourceGroups = useCatalogGroups(sourceId, languageId)

	const [search, setSearch] = useState("")
	const [picked, setPicked] = useState<Set<string>>(new Set())
	// сменили источник или назначение — старый выбор к новой паре не относится
	useEffect(() => setPicked(new Set()), [sourceId, catalogId])

	const found = useMemo(() => {
		const needle = search.trim().toLowerCase()
		const rows = sourceGroups.data ?? []
		if (!needle) return rows
		return rows.filter(
			row =>
				row.id.toLowerCase().includes(needle) ||
				(row.name ?? "").toLowerCase().includes(needle)
		)
	}, [sourceGroups.data, search])

	const action = useAction({
		describe: describeSiteError,
		after: () =>
			Promise.all([
				client.invalidateQueries({ queryKey: keys.catalogGroups(catalogId ?? undefined) }),
				client.invalidateQueries({ queryKey: keys.notReadyToDeploy() }),
			]),
	})

	async function attachSelected() {
		if (!catalog || picked.size === 0) return
		const count = picked.size
		const yes = await ask({
			title: `Добавить ${count} ${count === 1 ? "группу" : "групп"} в «${catalog.name}»?`,
			text: "Веб-категории выбранных групп скопируются в этот каталог, и им проставится вывод. Существующие строки не пострадают; обратной кнопки — открепить — здесь нет.",
			confirm: "Добавить",
		})
		if (!yes) return

		void action.run(async () => {
			const report = await catalogAdminWrites.attachGroups(catalog.id, [...picked])
			const rows = Object.values(report).reduce((sum, value) => sum + value, 0)
			const attachedGroups = Object.values(report).filter(value => value > 0).length
			setPicked(new Set())
			action.setNotice(
				attachedGroups > 0
					? `Добавлено ${attachedGroups} из ${count} — ${rows} строк веб-категорий. Остальные уже были в каталоге.`
					: `Все ${count} уже были в «${catalog.name}» — новых строк нет.`
			)
		})
	}

	async function attachWholeSource() {
		if (!catalog || !source || !matchedSite) return
		const yes = await ask({
			title: `Прикрепить весь каталог «${source.name}» к ${matchedSite.name}?`,
			text: `Все группы каталога-источника вместе с их веб-категориями попадут в каталог «${catalog.name}». Существующие строки не пострадают.`,
			confirm: "Прикрепить целиком",
		})
		if (!yes) return
		void action.run(
			() => websiteWrites.addCatalog(source.id, matchedSite.id),
			`Каталог «${source.name}» прикреплён к ${matchedSite.name}`
		)
	}

	const matchedSite = catalog
		? (sites.data ?? []).find(item => item.name === catalog.name) ?? null
		: null

	return (
		<div className="panel">
			<div className="panel__head">
				<span>Каталоги</span>
				<span className="panel__spacer" />
				<span className="muted">состав и назначение товаров</span>
			</div>

			<div className="panel__body">
				<label className="pub-bar__field">
					<span className="pub-bar__label">Каталог</span>
					<select
						className="select"
						value={catalogId ?? ""}
						onChange={event => setCatalogId(Number(event.target.value))}
					>
						{catalogs.map(item => (
							<option key={item.id} value={item.id}>
								{item.name}
								{matchedSite && item.id === catalogId ? " — сайт" : ""}
							</option>
						))}
					</select>
				</label>

				<p className="muted pub-lead">
					Что сейчас числится в каталоге <b>{catalog?.name ?? "—"}</b>:
				</p>
				<GroupsTable rows={composition.data ?? []} loading={composition.isLoading} />

				<details className="pub-fold">
					<summary><b>Добавить товары из другого каталога</b></summary>
					<div className="pub-ready">
						<div className="pub-actions">
							<select
								className="select"
								value={sourceId ?? ""}
								onChange={event => setSourceId(Number(event.target.value))}
							>
								<option value="">— каталог-источник —</option>
								{others.map(item => (
									<option key={item.id} value={item.id}>
										{item.name}
									</option>
								))}
							</select>

							{source && matchedSite && (
								<button
									className="btn"
									disabled={disabled || action.busy}
									onClick={() => void attachWholeSource()}
									title="Один запрос: переносит все группы каталога-источника разом (SiteCatalogService.AddCatalogToSite)"
								>
									Прикрепить весь каталог
								</button>
							)}
						</div>

						{source && (
							<>
								<input
									className="input"
									placeholder="Поиск по коду или названию"
									value={search}
									onChange={event => setSearch(event.target.value)}
								/>

								<GroupsTable
									rows={found}
									loading={sourceGroups.isLoading}
									selected={picked}
									onToggle={id =>
										setPicked(prev => {
											const next = new Set(prev)
											if (next.has(id)) next.delete(id)
											else next.add(id)
											return next
										})
									}
								/>

								<div className="pub-actions">
									<button
										className="btn"
										disabled={found.length === 0}
										onClick={() => setPicked(new Set(found.map(row => row.id)))}
									>
										Выбрать найденные ({found.length})
									</button>
									<button
										className="btn"
										disabled={picked.size === 0}
										onClick={() => setPicked(new Set())}
									>
										Снять выбор
									</button>

									<span className="pub-actions__spacer" />

									<span className="muted">{picked.size} выбрано</span>
									<button
										className="btn btn--primary"
										disabled={disabled || action.busy || picked.size === 0}
										onClick={() => void attachSelected()}
									>
										Добавить выбранные
									</button>
									{action.busy && <span className="spinner" />}
								</div>
							</>
						)}
					</div>
				</details>

				{dialog}
				<ActionNotes action={action} />

				{catalog && matchedSite && (
					<NotReady
						catalog={catalog}
						siteCatalog={catalog}
						websiteId={matchedSite.id}
						disabled={disabled}
					/>
				)}
				{catalog && !matchedSite && (
					<Note tone="hint">
						«{catalog.name}» не сайтовый каталог (сайта с таким именем нет в
						`WebSites`) — проверка готовности к выгрузке тут ни при чём, она
						только для каталогов сайтов.
					</Note>
				)}
			</div>
		</div>
	)
}

/** Таблица групп каталога. Без `onToggle` — просто список (состав каталога);
 *  с ним — с чекбоксами (выбор групп для добавления). */
function GroupsTable({
	rows,
	loading,
	selected,
	onToggle,
}: {
	rows: CatalogGroupRow[]
	loading: boolean
	selected?: Set<string>
	onToggle?: (id: string) => void
}) {
	if (loading) {
		return (
			<div className="pub-ready__head">
				<span className="spinner" /> <span className="muted">Считаю…</span>
			</div>
		)
	}

	if (rows.length === 0) {
		return <p className="muted">Пусто.</p>
	}

	const shown = rows.slice(0, LIMIT)

	return (
		<>
			<div className="pub-ready__wrap">
				<table className="products">
					<thead>
						<tr>
							{onToggle && <th className="col-check" />}
							<th className="col-code">Код</th>
							<th>Название</th>
							<th className="col-reason">Вывод</th>
						</tr>
					</thead>
					<tbody>
						{shown.map(row => (
							<tr
								key={row.id}
								className={onToggle ? "row-pickable" : undefined}
								onClick={onToggle ? () => onToggle(row.id) : undefined}
							>
								{onToggle && (
									<td>
										<input
											type="checkbox"
											checked={selected?.has(row.id) ?? false}
											onChange={() => onToggle(row.id)}
											onClick={event => event.stopPropagation()}
										/>
									</td>
								)}
								<td className="mono">{row.id}</td>
								<td>{row.name ?? "—"}</td>
								<td className="muted">{row.showStatus ? "да" : "нет"}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<div className="muted">
				{rows.length > LIMIT
					? `показаны первые ${LIMIT} из ${rows.length}`
					: `строк: ${rows.length}`}
			</div>
		</>
	)
}
